import { Table } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type RouterListDataFragment } from '../../../api/fragments/RouterListData';
import { useListRoutersQuery } from '../../../api/queries/listRouters';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';

const renderCompanyName = (value: any, record: RouterListDataFragment) => record.company.displayName;

export type RouterListProps = {
    onCreate: () => void;
};

const RouterList = ({ onCreate }: RouterListProps) => {
    const { t } = useTranslation('routerList');

    // get company context
    const company = useCompany(true);

    const navigate = useNavigate();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Router);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize } = state;
    const { data, loading, error } = useListRoutersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            filter: { companyId: company?.id },
        },
    });

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.list?.count || 0;

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onRow={(row: RouterListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/system/routers/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('routerList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="companyName"
                    dataIndex="company.name"
                    render={renderCompanyName}
                    title={t('routerList:columns.companyName')}
                />
            )}
            <Table.Column key="hostname" dataIndex="hostname" title={t('routerList:columns.hostname')} />
            <Table.Column key="pathname" dataIndex="pathname" title={t('routerList:columns.pathname')} />
            <Table.Column
                key="withAdmin"
                align="center"
                dataIndex="withAdmin"
                render={value => renderBooleanIcon(value, true)}
                title={t('routerList:columns.withAdmin')}
                width={150}
            />
        </PaginatedTableWithContext>
    );
};

export default RouterList;
